import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import {
    CONFIRMATION_TYPES,
    getUuidV4,
    ModalClass,
    NotificationIdState,
    removeConfirmation,
    selectConfirmationExists,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { LogInstanceToken } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { of } from 'rxjs';
import { defaultIfEmpty, take, tap } from 'rxjs/operators';

/**
 * A notification holds at most one confirmation: a short and a full one exclude each other. The slice
 * stores them under separate identities, though, so opening one type while the other is stored would
 * leave both side by side. Instead the technician is asked whether to discard the stored one — the
 * data they entered there is lost. Declining keeps it and cancels the navigation.
 *
 * Guarding the route rather than the buttons covers every way in: both status step implementations,
 * the timesheet's link and stale links alike. It reads the type from the URL, so a short confirmation
 * rerouted to a full one (a cancelled notification) is checked against the type actually opened.
 *
 * Runs behind `ConfirmationIsLoadedGuard`, so the slice is settled and can be read synchronously.
 */
export const ConfirmationTypeChangeGuard: CanActivateFn = (route) => {
    const notificationId: NotificationIdState = {
        frontId: route.paramMap.get('frontid') || '',
        qmNum: route.paramMap.get('qmnum') || '',
        maNum: route.paramMap.get('manum') || '',
    };
    const requestedType = (route.paramMap.get('crmtype') as CONFIRMATION_TYPES) || CONFIRMATION_TYPES.NORMAL;
    const otherType = requestedType === CONFIRMATION_TYPES.SHORT ? CONFIRMATION_TYPES.NORMAL : CONFIRMATION_TYPES.SHORT;

    const store = inject(Store);
    if (!store.selectSignal(selectConfirmationExists(notificationId, otherType))()) {
        return true;
    }

    const modalService = inject(BsModalService);
    const translate = inject(TranslateService);
    const log = inject(LogInstanceToken);

    const modalInstance = modalService.show(YesNoModalDialogComponent, {
        backdrop: 'static',
        class: ModalClass.MODAL_MEDIUM,
        initialState: {
            model: new YesNoModalDialogModel(
                <string>translate.instant('CONFIRMATION_TYPE_CHANGE_TITLE'),
                <string>(
                    translate.instant(
                        requestedType === CONFIRMATION_TYPES.SHORT
                            ? 'CONFIRMATION_TYPE_CHANGE_TO_SHORT_BODY'
                            : 'CONFIRMATION_TYPE_CHANGE_TO_FULL_BODY',
                    )
                ),
                <string>translate.instant('CONFIRMATION_TYPE_CHANGE_YES_BUTTON'),
                <string>translate.instant('CONFIRMATION_TYPE_CHANGE_NO_BUTTON'),
            ),
        },
        ariaLabelledBy: 'yes-no-modal-title',
    });

    return (modalInstance.content?.modalResult ?? of(false)).pipe(
        take(1),
        // The dialog completes its subject on every button; should it ever close without a result,
        // keeping the stored confirmation is the safe outcome.
        defaultIfEmpty(false),
        tap((isConfirmed) => {
            if (!isConfirmed) {
                return;
            }
            log.info(
                `ConfirmationTypeChangeGuard: discarding the ${otherType} confirmation of notification ${notificationId.frontId}/${notificationId.qmNum}/${notificationId.maNum} to open a ${requestedType} one`,
            );
            store.dispatch(
                removeConfirmation({ notificationId, confirmationType: otherType, correlationId: getUuidV4() }),
            );
        }),
    );
};
